/**
 * Where the server is — one notion of it, asked by everything that needs one.
 *
 * # Why this exists
 *
 * Five different things in this frontend build a URL out of the page's own
 * location: the socket (`realtimeUrl`), the session lock (which the generated
 * client derives from the socket's host), a clip's `<audio src>`, the join QR
 * and the recap QR. In a browser that is **correct** — phase 9's whole finding
 * was that the page, the API, the socket and the lock must be one origin, and
 * deriving is what makes that true on a laptop, on a LAN address and behind
 * nginx without anybody editing a constant.
 *
 * In a packaged television app (phase 11) the page is loaded from the local
 * filesystem, so `location.host` is `""` and `location.origin` is the string
 * `"null"`. Phase 11a measured what that does, and it is not one broken URL but
 * five: `ws:///rpc`, `http:///spheromak/lock.json`, `file:///api/clip/{sha}`,
 * `null/party/BKGZ` and `null/recap/{id}` — a television playing silence behind
 * an unscannable code. Fixing the socket alone would have looked like it worked.
 *
 * # The rule
 *
 * **A page that is served talks to the server that served it, and nothing can
 * override that.** That is phase 9's single-origin property, and an override
 * that could point a served page elsewhere would break the session cookie and
 * the CORS posture silently — the failure mode this project keeps meeting.
 *
 * When there is no origin to derive from (`file:`, `app:`, `about:`), the answer
 * has to be configured:
 *
 * 1. a runtime origin, set by {@link setServerOrigin} and kept in `localStorage`,
 *    so a household points the app at its own server once rather than at every
 *    boot;
 * 2. `VITE_SONOLUME_SERVER_ORIGIN`, baked in when the package is built for a
 *    known deployment;
 * 3. otherwise **{@link NoServerOrigin}, loudly** — because the alternative is
 *    the exact failure phase 9 spent a session finding: a socket that never
 *    opens, with nothing in the console.
 */

/**
 * Just enough of `Location` to answer the question, so a caller can pass one
 * that does not exist in this environment — a `file://` page cannot be created
 * in jsdom, and it is the case that matters most here.
 */
export interface PageLocation {
  /** `"http:"`, `"https:"`, `"file:"` … */
  protocol: string;
  /** Host and port, and the **empty string** for a `file://` page. */
  host: string;
}

/**
 * The app has no idea where its server is.
 *
 * Thrown rather than papered over with a default: a wrong default produces a
 * screen that looks like a network problem, and somebody spends an evening on
 * their router. A screen that catches this must say what is missing.
 */
export class NoServerOrigin extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NoServerOrigin";
  }
}

/** Where a runtime-configured origin is kept. */
const STORAGE_KEY = "sonolume.server_origin";

/** Is this page served by something we can talk to? */
function isServed(page: PageLocation): boolean {
  return (page.protocol === "http:" || page.protocol === "https:") && page.host !== "";
}

/**
 * Was this page served, rather than loaded from a package?
 *
 * The public form of the question above, for callers that need to know whether
 * the page has an origin *at all* rather than what it is — registering a service
 * worker, for one (phase 13's PWA): a `file://` page has no origin to scope one
 * to, and asking would throw where doing nothing is correct.
 */
export function isServedPage(page: PageLocation = window.location): boolean {
  return isServed(page);
}

/**
 * Validate a configured origin and reduce it to scheme and host.
 *
 * A trailing path is rejected rather than accepted and ignored: every URL in
 * this frontend is an absolute path from the root (`/api/...`, `/rpc`,
 * `/party/CODE`), so `https://host/sonolume` cannot mean what whoever typed it
 * expected, and silently dropping the segment is how they would find out in
 * round one.
 */
function normalise(value: string, source: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new NoServerOrigin(`${source} is not a URL: ${JSON.stringify(value)}`);
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new NoServerOrigin(
      `${source} must be http or https, not ${url.protocol} (${value})`,
    );
  }
  if (url.pathname !== "/" && url.pathname !== "") {
    throw new NoServerOrigin(`${source} must be an origin with no path, not ${value}`);
  }
  return `${url.protocol}//${url.host}`;
}

/** The build-time origin, for a package shipped against a known deployment. */
function buildTimeOrigin(): string | null {
  const configured = import.meta.env.VITE_SONOLUME_SERVER_ORIGIN;
  return configured === undefined || configured === "" ? null : configured;
}

/** The runtime origin, if one was ever set on this device. */
function storedOrigin(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be disabled or unavailable; that is "not configured", not a
    // crash on the first render.
    return null;
  }
}

/**
 * Point this installation at a server, for good.
 *
 * Only ever consulted by a page that cannot derive an origin, so calling it in a
 * browser changes nothing — see the rule at the top of this file. Pass `null` to
 * forget.
 */
export function setServerOrigin(origin: string | null): void {
  try {
    if (origin === null) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      STORAGE_KEY,
      normalise(origin, "the configured server origin"),
    );
  } catch (cause) {
    if (cause instanceof NoServerOrigin) {
      throw cause;
    }
    // Storage refused. Nothing sensible to do, and it must not take the screen
    // down with it.
  }
}

/** What was configured, strongest first, or `null` if nothing was. */
export function configuredServerOrigin(): string | null {
  const stored = storedOrigin();
  if (stored !== null && stored !== "") {
    return normalise(stored, "the configured server origin");
  }
  const built = buildTimeOrigin();
  return built === null ? null : normalise(built, "VITE_SONOLUME_SERVER_ORIGIN");
}

/**
 * The origin, derived or configured — the pure half, so every branch is
 * testable without a `file://` page.
 */
export function resolveServerOrigin(
  page: PageLocation,
  configured: string | null,
): string {
  if (isServed(page)) {
    return `${page.protocol}//${page.host}`;
  }
  if (configured === null) {
    throw new NoServerOrigin(
      `This app has not been told where sonolume is. The page was loaded from ` +
        `${page.protocol}, which has no origin to derive one from, and none is ` +
        `configured (VITE_SONOLUME_SERVER_ORIGIN, or setServerOrigin at runtime).`,
    );
  }
  return normalise(configured, "the configured server origin");
}

/** The server's origin — `https://sonolume.example`, with no trailing slash. */
export function serverOrigin(page: PageLocation = window.location): string {
  return resolveServerOrigin(page, configuredServerOrigin());
}

/**
 * Whether there is an answer at all.
 *
 * For a screen that would rather render "this app has not been set up" than
 * throw out of a render or, worse, try to connect to nowhere.
 */
export function hasServerOrigin(page: PageLocation = window.location): boolean {
  try {
    serverOrigin(page);
    return true;
  } catch {
    return false;
  }
}

/**
 * A URL for something the server serves — **relative when the page is served by
 * it**, absolute when it is not.
 *
 * Relative on purpose rather than as an optimisation: a same-origin request is
 * what makes the `HttpOnly; SameSite=Lax` session and device cookies ride along
 * at all, and it is what keeps the browser build's behaviour byte-identical to
 * what phases 3 through 10 verified.
 */
export function serverUrl(path: string, page: PageLocation = window.location): string {
  return isServed(page) ? path : `${serverOrigin(page)}${path}`;
}

/**
 * An absolute link into the app itself — a QR code, or something to paste into a
 * call.
 *
 * Always absolute: a relative URL in a QR code is not a link, and on a
 * television the page's own address is a path inside a package.
 */
export function appUrl(path: string, page: PageLocation = window.location): string {
  return `${serverOrigin(page)}${path}`;
}
